import {
    Alert,
    AlertIcon,
    Divider,
    Flex,
    Heading,
    HStack,
    IconButton,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { createMappings } from '../../../canopy-ui-components/api/mappings';
import CanopyButton from '../../../canopy-ui-components/components/CanopyButton/CanopyButton';
import UserDetails, { UserDetailsType } from './UserDetails';
import WorkbookDetails, { WorkbookDetailsType } from './WorkbookDetails';

import { useCurrentColors } from '../../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
import { useCheckForMobileOrTablet } from '../../../canopy-ui-components/hooks/UseResponsive/UseResponsive';
import ActClose from './../../../canopy-ui-components/components/Icons/ActClose';

export type AnalyticsMapping = {
    userDetails: UserDetailsType;
    workbookDetails: WorkbookDetailsType[];
};

enum State {
    info = 'info',
    warning = 'warning',
    success = 'success',
    error = 'error',
}

interface Status {
    state?: State;
    message: string | null;
}

interface Props extends PageComponentProps<any> {
    onSave: () => void;
    refreshData: () => void;
    initialMapping?: AnalyticsMapping[];
}

function CreateForm(props: Props) {
    const { t } = props?.globalProps || {};
    const initial =
        props?.initialMapping && props?.initialMapping?.length > 0
            ? props.initialMapping
            : [
                  {
                      userDetails: {
                          userId: 0,
                          adminId: 0,
                          userName: '',
                          paOrRmName: '',
                          deviceType: 'desktop',
                      },
                      workbookDetails: [
                          {
                              subMenuKey: '',
                              workbookPath: '',
                              defaultView: '',
                          },
                      ],
                  },
              ];
    const currentColors = useCurrentColors();
    const [status, setStatus] = useState<Status[]>();
    const isMobileOrTablet = useCheckForMobileOrTablet();
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
    const [analyticsMappings, setAnalyticsMappings] =
        useState<AnalyticsMapping[]>(initial);

    useEffect(() => {
        setIsSubmitButtonDisabled(false);
        analyticsMappings?.forEach((mapping) => {
            if (
                !(
                    mapping.userDetails.userName &&
                    mapping.userDetails.deviceType
                )
            ) {
                setIsSubmitButtonDisabled(true);
                return;
            }
            mapping.workbookDetails.forEach((workbook) => {
                if (!(workbook.subMenuKey && workbook.workbookPath)) {
                    setIsSubmitButtonDisabled(true);
                    return;
                }
            });
        });
        setStatus(undefined);
    }, [analyticsMappings]);

    const handleUserDetailsUpdate = (
        userDetails: UserDetailsType,
        index: number
    ): void => {
        const analyticsMappingsArr = [...analyticsMappings];
        analyticsMappingsArr[index] = {
            ...analyticsMappingsArr[index],
            userDetails: { ...userDetails },
        };
        setAnalyticsMappings(analyticsMappingsArr);
    };

    const handleWorkbookDetailsUpdate = (
        workbookDetails: WorkbookDetailsType,
        mappingsIndex: number,
        workbookIndex: number
    ): void => {
        const analyticsMappingsArr = [...analyticsMappings];
        analyticsMappingsArr[mappingsIndex].workbookDetails[workbookIndex] = {
            ...workbookDetails,
        };
        setAnalyticsMappings(analyticsMappingsArr);
    };

    const handleAddMappings = (): void => {
        setAnalyticsMappings([
            ...analyticsMappings,
            {
                userDetails: {
                    userId: 0,
                    adminId: 0,
                    userName: '',
                    paOrRmName: '',
                    deviceType: 'desktop',
                },
                workbookDetails: [
                    {
                        subMenuKey: '',
                        workbookPath: '',
                        defaultView: '',
                    },
                ],
            },
        ]);
    };

    const handleAddWorkbook = (mappingsIndex: number): void => {
        const newAnalyticsMappings = analyticsMappings.map(
            (mapping, mIndex) => {
                if (mIndex === mappingsIndex) {
                    return {
                        ...mapping,
                        workbookDetails: [
                            ...mapping.workbookDetails,
                            {
                                subMenuKey: '',
                                workbookPath: '',
                                defaultView: '',
                            },
                        ],
                    };
                } else {
                    return mapping;
                }
            }
        );
        setAnalyticsMappings(newAnalyticsMappings);
    };

    const handleDeleteWorkbook = (
        mappingsIndex: number,
        workbookIndex: number
    ): void => {
        const newAnalyticsMappings = analyticsMappings.map(
            (mapping, mIndex) => {
                if (mIndex === mappingsIndex) {
                    return {
                        ...mapping,
                        workbookDetails: [
                            ...mapping.workbookDetails.slice(0, workbookIndex),
                            ...mapping.workbookDetails.slice(workbookIndex + 1),
                        ],
                    };
                } else {
                    return mapping;
                }
            }
        );
        setAnalyticsMappings(newAnalyticsMappings);
    };
    const handleDeleteMapping = (mappingIndex: number) => {
        setAnalyticsMappings([
            ...analyticsMappings.slice(0, mappingIndex),
            ...analyticsMappings.slice(mappingIndex + 1),
        ]);
    };

    const handleSaveMappings = async () => {
        const statusArr: Status[] = analyticsMappings.map(() => {
            return {
                state: undefined,
                message: t('engine.status.processing'),
            };
        });
        setStatus(statusArr);
        try {
            const result = await Promise.all(
                analyticsMappings.map(async (mapping, index) => {
                    const data: any = {
                        user_id: mapping.userDetails.userId,
                        device: mapping.userDetails.deviceType,
                        submenu_workbook: mapping.workbookDetails.map(
                            (item) => {
                                return {
                                    submenu_key: item.subMenuKey,
                                    workbook_view_path: item.workbookPath,
                                };
                            }
                        ),
                    };
                    if (mapping.userDetails.adminId !== 0) {
                        data['admin_id'] = mapping.userDetails.adminId;
                    }
                    return await createMappings(data);
                })
            );
            let temp_status: Status[] = [];
            result.map((response, index) => {
                temp_status.push({
                    state:
                        response?.state == 'error'
                            ? State.error
                            : State.success,
                    message:
                        response?.state == 'error'
                            ? response?.message
                            : t('ui.analytics-mappings.create-success'),
                });
            });
            setStatus(temp_status);
            if (temp_status.every((res: any) => res?.state == State.success)) {
                setTimeout(() => {
                    setAnalyticsMappings([
                        {
                            userDetails: {
                                userId: 0,
                                adminId: 0,
                                userName: '',
                                paOrRmName: '',
                                deviceType: 'desktop',
                            },
                            workbookDetails: [
                                {
                                    subMenuKey: '',
                                    workbookPath: '',
                                    defaultView: '',
                                },
                            ],
                        },
                    ]);
                    setStatus(undefined);
                    props?.refreshData && props?.refreshData();
                    props?.onSave && props?.onSave();
                }, 1000);
            }
        } catch (err) {
            console.log('main error:', err);
        }
    };

    return (
        <>
            {!isMobileOrTablet && (
                <Flex>
                    <Flex
                        width="35%"
                        height="100%">
                        <Heading
                            as="h6"
                            size="md"
                            my="10px"
                            mx="0">
                            {t('user-details')}
                        </Heading>
                    </Flex>
                    <Flex
                        width="82%"
                        // minWidth={['100%', '100%', '100%', '70%', '80%']}
                        height="100%">
                        <Heading
                            as="h6"
                            size="md"
                            my="10px"
                            mx="0">
                            {t('workbook-details')}
                        </Heading>
                    </Flex>
                </Flex>
            )}
            <VStack
                spacing="30px"
                width="100%">
                {analyticsMappings?.map(
                    ({ userDetails, workbookDetails }, mappingsIndex) => {
                        return (
                            <Flex
                                key={mappingsIndex}
                                width="100%"
                                border={`1px solid ${currentColors.componentBorder}`}>
                                <Flex
                                    align="flex-start"
                                    width="100%"
                                    p="10px"
                                    direction={[
                                        'column',
                                        'column',
                                        'column',
                                        'column',
                                        'row',
                                    ]}>
                                    {isMobileOrTablet && (
                                        <Heading
                                            as="h6"
                                            size="md"
                                            m="0"
                                            mt="10px"
                                            px="20px"
                                            alignSelf="flex-start">
                                            {t('user-details')}
                                        </Heading>
                                    )}
                                    <HStack
                                        align="flex-start"
                                        mr="10px"
                                        width={[
                                            '100%',
                                            '100%',
                                            '100%',
                                            '100%',
                                            '40%',
                                        ]}
                                        justifyContent="space-between">
                                        <UserDetails
                                            data={userDetails}
                                            initialData={userDetails}
                                            mappingsIndex={mappingsIndex}
                                            updateUserDetails={
                                                handleUserDetailsUpdate
                                            }
                                            {...props}></UserDetails>
                                        {isMobileOrTablet &&
                                            analyticsMappings.length > 1 && (
                                                <IconButton
                                                    alignSelf="flex-start"
                                                    background="transparent"
                                                    cursor="pointer"
                                                    border="none"
                                                    color={
                                                        currentColors
                                                            .whiteAlpha['400']
                                                    }
                                                    icon={<ActClose />}
                                                    aria-label="delete-mapping"
                                                    onClick={() =>
                                                        handleDeleteMapping(
                                                            mappingsIndex
                                                        )
                                                    }
                                                    variant="ghost"
                                                    _hover={{
                                                        backgroundColor:
                                                            'transparent',
                                                    }}
                                                    _focus={{
                                                        border: '0px',
                                                        backgroundColor:
                                                            'transparent',
                                                        outline: 'none',
                                                    }}
                                                    _active={{ border: '0px' }}
                                                />
                                            )}
                                    </HStack>
                                    {!isMobileOrTablet && (
                                        <Divider
                                            borderColor={
                                                currentColors.brand['700']
                                            }
                                            height="calc(100% - 20px)"
                                            mr="20px"
                                            orientation="vertical"
                                        />
                                    )}
                                    <VStack
                                        spacing="15px"
                                        width="100%"
                                        justifyContent="flex-start"
                                        pt="10px"
                                        pr="10px">
                                        {isMobileOrTablet && (
                                            <>
                                                <Divider
                                                    borderColor={
                                                        currentColors.brand[
                                                            '700'
                                                        ]
                                                    }
                                                    width="calc(100% - 40px)"
                                                    mx="20px"
                                                />
                                                <Heading
                                                    as="h6"
                                                    size="md"
                                                    m="0"
                                                    px="20px"
                                                    alignSelf="flex-start">
                                                    {t('workbook-details')}
                                                </Heading>
                                            </>
                                        )}
                                        {status &&
                                            status[mappingsIndex]?.message && (
                                                <Alert
                                                    status={
                                                        status[mappingsIndex]
                                                            ?.state
                                                    }
                                                    height="100%">
                                                    <AlertIcon />
                                                    {
                                                        status[mappingsIndex]
                                                            ?.message
                                                    }
                                                </Alert>
                                            )}
                                        <HStack
                                            width="100%"
                                            spacing="20px"
                                            justifyContent="end">
                                            <CanopyButton
                                                onClick={() =>
                                                    handleAddWorkbook(
                                                        mappingsIndex
                                                    )
                                                }
                                                height="30px"
                                                data-test="add-workbook"
                                                variant="outline"
                                                alignSelf="flex-end"
                                                fontSize="sm">
                                                {t('ui.mm.add-workbook')}
                                            </CanopyButton>
                                            {!isMobileOrTablet &&
                                                analyticsMappings.length >
                                                    1 && (
                                                    <Flex
                                                        height="30px"
                                                        alignItems="center">
                                                        <Tooltip
                                                            aria-label="delete"
                                                            label={t(
                                                                'ui.analytics-mappings.delete-mapping'
                                                            )}
                                                            placement="bottom-start"
                                                            hasArrow>
                                                            <IconButton
                                                                background="transparent"
                                                                cursor="pointer"
                                                                border="none"
                                                                width="20px !important"
                                                                minWidth="20px !important"
                                                                height="20px !important"
                                                                icon={
                                                                    <ActClose />
                                                                }
                                                                aria-label="delete"
                                                                data-test="delete"
                                                                backgroundColor="transparent"
                                                                color={
                                                                    currentColors
                                                                        .brand[
                                                                        '300'
                                                                    ]
                                                                }
                                                                variant="ghost"
                                                                _hover={{
                                                                    backgroundColor:
                                                                        'transparent',
                                                                }}
                                                                _focus={{
                                                                    border: '0px',
                                                                    backgroundColor:
                                                                        'transparent',
                                                                    outline:
                                                                        'none',
                                                                }}
                                                                _active={{
                                                                    border: '0px',
                                                                }}
                                                                onClick={() =>
                                                                    handleDeleteMapping(
                                                                        mappingsIndex
                                                                    )
                                                                }
                                                            />
                                                        </Tooltip>
                                                    </Flex>
                                                )}
                                        </HStack>
                                        {workbookDetails?.map(
                                            (workbookDetail, index) => {
                                                return (
                                                    <WorkbookDetails
                                                        total={
                                                            workbookDetails.length
                                                        }
                                                        data={workbookDetail}
                                                        mappingsIndex={
                                                            mappingsIndex
                                                        }
                                                        workbookIndex={index}
                                                        globalProps={
                                                            props?.globalProps
                                                        }
                                                        updateWorkbookDetails={
                                                            handleWorkbookDetailsUpdate
                                                        }
                                                        handleDeleteWorkbook={
                                                            handleDeleteWorkbook
                                                        }
                                                        key={
                                                            index
                                                        }></WorkbookDetails>
                                                );
                                            }
                                        )}
                                    </VStack>
                                </Flex>
                            </Flex>
                        );
                    }
                )}
                <HStack
                    spacing="15px"
                    pb="30px"
                    alignSelf="flex-end"
                    justify="flex-end">
                    <CanopyButton
                        onClick={handleAddMappings}
                        height="30px"
                        data-test="add-mappings"
                        variant="outline"
                        alignSelf="flex-end"
                        fontSize="sm"
                        // isDisabled={isSubmitButtonDisabled}
                    >
                        {t('ui.mm.add-mappings')}
                    </CanopyButton>
                    <CanopyButton
                        onClick={handleSaveMappings}
                        height="30px"
                        data-test="save-mappings"
                        alignSelf="flex-end"
                        fontSize="sm"
                        isDisabled={isSubmitButtonDisabled}>
                        {t('save')}
                    </CanopyButton>
                </HStack>
            </VStack>
        </>
    );
}

export default CreateForm;
